import React, { useEffect, useState } from "react";
import {
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Table,
  Tag,
  Switch,
  InputNumber,
  Button,
  Space,
  Spin,
  message,
} from "antd";

import {
  privateGetApi,
  privatePutApi,
  privateDeleteApi,
} from "../../../utils/apiHelper";

const { Paragraph, Text } = Typography;

// PRD item 1 (FR-1.11, FR-1.7), task 1.10. Settings → Deliverability:
// complaint/bounce gauges, suppression list, inactive-sunset toggle with
// a live preview count before enabling.
export default function DeliverabilitySettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suppressions, setSuppressions] = useState([]);
  const [suppressionsLoading, setSuppressionsLoading] = useState(true);

  const [sunsetEnabled, setSunsetEnabled] = useState(false);
  const [sunsetDays, setSunsetDays] = useState(180);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchDeliverability = () => {
    setLoading(true);
    privateGetApi("/auth/deliverability", {})
      .then((res) => {
        const result = res?.data?.result || null;
        setData(result);
        if (result?.inactiveSunsetDays) {
          setSunsetEnabled(true);
          setSunsetDays(result.inactiveSunsetDays);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const fetchSuppressions = () => {
    setSuppressionsLoading(true);
    privateGetApi("/contacts/suppressions", {})
      .then((res) => {
        setSuppressions(res?.data?.result?.records || []);
        setSuppressionsLoading(false);
      })
      .catch(() => setSuppressionsLoading(false));
  };

  useEffect(() => {
    fetchDeliverability();
    fetchSuppressions();
  }, []);

  const runPreview = (days) => {
    if (!days || days <= 0) {
      setPreviewCount(null);
      return;
    }
    setPreviewLoading(true);
    privateGetApi(
      `/auth/deliverability?previewInactiveSunsetDays=${days}`,
      {},
    )
      .then((res) => {
        setPreviewCount(res?.data?.result?.inactivePreviewCount ?? null);
        setPreviewLoading(false);
      })
      .catch(() => setPreviewLoading(false));
  };

  const saveSunsetSetting = async () => {
    setSaving(true);
    try {
      await privatePutApi("/auth/deliverability", {
        inactiveSunsetDays: sunsetEnabled ? sunsetDays : null,
      });
      message.success("Deliverability settings saved.");
      fetchDeliverability();
    } catch {
      // toast already shown
    } finally {
      setSaving(false);
    }
  };

  const unsuppress = async (id) => {
    try {
      await privateDeleteApi(`/contacts/suppressions/${id}`);
      message.success("Contact removed from the suppression list.");
      fetchSuppressions();
    } catch {
      // toast already shown
    }
  };

  return (
    <div style={{ margin: "1.5rem 2rem" }}>
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <Card title="Sending health">
            {loading && !data ? (
              <Spin />
            ) : !data ? (
              <Text type="secondary">Failed to load deliverability status.</Text>
            ) : (
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                {data.pausedAt && (
                  <Alert
                    type="error"
                    showIcon
                    message="Sending paused"
                    description={
                      data.pausedReason ||
                      "Your complaint rate is too high on the shared sending address. Contact support, or verify your own domain under Customizations."
                    }
                  />
                )}
                {!data.pausedAt && data.warningAt && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Elevated complaint rate"
                    description="Your complaint rate is above the warning threshold (0.1% over the last 7 days). Review your list quality and consent practices."
                  />
                )}
                <Row gutter={[16, 16]}>
                  <Col xs={12} md={6}>
                    <Statistic
                      title="Complaint rate (7d)"
                      value={(data.complaintRate7d * 100).toFixed(2)}
                      suffix="%"
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic
                      title="Bounce rate (7d)"
                      value={(data.bounceRate7d * 100).toFixed(2)}
                      suffix="%"
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic
                      title="Suppressed contacts"
                      value={data.suppressedCount}
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic
                      title="Inactive contacts"
                      value={data.inactiveCount}
                    />
                  </Col>
                </Row>
              </Space>
            )}
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Inactive-subscriber sunset">
            <Paragraph type="secondary">
              Contacts who've received at least a few emails with no opens
              or clicks in the window below are tagged &ldquo;inactive&rdquo;
              and automatically skipped on future sends — never deleted, and
              automatically un-tagged if they engage again.
            </Paragraph>
            <Space align="center" style={{ marginBottom: 12 }}>
              <Switch
                checked={sunsetEnabled}
                onChange={(checked) => {
                  setSunsetEnabled(checked);
                  if (checked) runPreview(sunsetDays);
                  else setPreviewCount(null);
                }}
              />
              <Text>Enable inactive-subscriber sunset</Text>
            </Space>
            {sunsetEnabled && (
              <div style={{ marginBottom: 16 }}>
                <Space align="center">
                  <Text>Window:</Text>
                  <InputNumber
                    min={1}
                    value={sunsetDays}
                    onChange={(value) => {
                      setSunsetDays(value);
                      runPreview(value);
                    }}
                    addonAfter="days"
                  />
                  {previewLoading ? (
                    <Spin size="small" />
                  ) : previewCount !== null ? (
                    <Text type="secondary">
                      {previewCount} contact(s) would be tagged inactive
                      right now.
                    </Text>
                  ) : null}
                </Space>
              </div>
            )}
            <Button type="primary" loading={saving} onClick={saveSunsetSetting}>
              Save
            </Button>
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Suppression list">
            <Paragraph type="secondary">
              Addresses that hard-bounced, complained or repeatedly soft-
              bounced. They're skipped on every future send until removed
              here.
            </Paragraph>
            <Table
              size="small"
              loading={suppressionsLoading}
              rowKey="_id"
              dataSource={suppressions}
              pagination={{ pageSize: 10 }}
              columns={[
                { title: "Email", dataIndex: "emailAddress" },
                {
                  title: "Reason",
                  dataIndex: "reason",
                  render: (reason) => <Tag>{reason}</Tag>,
                },
                {
                  title: "Since",
                  dataIndex: "createdAt",
                  render: (value) => new Date(value).toLocaleString(),
                },
                {
                  title: "",
                  key: "actions",
                  render: (_, row) => (
                    <Button size="small" onClick={() => unsuppress(row._id)}>
                      Remove
                    </Button>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
